import React, { useState, useEffect, useRef } from 'react';

import { 
  DisablableOption,
} from "@models";
import { 
  Character,
  CharacterItem,
  CharacterContainer,
  Armor,
  Campaign,
  CampaignInventory,
  CharacterInventory,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ViewItem from "@dndComponents/display/ViewItem";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import Grid from '@mui/material/Grid2';

interface Props {
  obj: Character | Campaign;
  inventory: CharacterInventory | CampaignInventory;
  item: CharacterItem;
  disabled: boolean;
  onOpen: (open_item: CharacterItem) => void;
  reload_time: number;
  log_it?: boolean;
}

const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterViewInventoryItem({
  obj,
  inventory,
  item,
  disabled,
  onOpen,
  reload_time,
  log_it
}: Props) {
  const [prev_reload_time, setPrevReloadTime] = useState(0);
  // const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
      },
    });
  });

  useEffect(() => {
    if (reload_time !== prev_reload_time) {
      setPrevReloadTime(reload_time);
    }
  }, [reload_time, prev_reload_time]);

  const { classes, cx } = useStyles();

  const renderTake = () => {
    return (
      <FlexBox xs_expand={0} style={{ width: "100px" }}>
        { inventory instanceof CampaignInventory && obj instanceof Character && obj.campaign && inventory.items.some(o => o.true_id === item.true_id) &&
          <DandDButtonBox
            name="Take"
            small
            // log_it
            disabled={processing}
            onClick={ async () => {
              // debugger;
              setProcessing(true);
              await connect_util.moveItem(obj, inventory, item, "Take");
              char_util.recalcWeights(obj);
            }}
          />
        }
      </FlexBox>
    );
  }

  const renderMoveTo = () => {
    let options: DisablableOption[] = []; 

    if (inventory.items.some(o => o.true_id === item.true_id)) {
      options = []; item instanceof CharacterContainer ? [] : inventory.containers.filter(o => 
        item.in_char_container_id !== o.true_id).map(o => { return { text: o.name, id: o.true_id }});
      if (inventory instanceof CampaignInventory) {
        if (obj instanceof Campaign) {
          // This is the Campaign Inventory as seen by the GM, 
          // so we want to add the ability to send to any Character in the Campaign (through Mail).
          options.push(...obj.characters.map(char => {
            const opt: DisablableOption = {
              id: `char_id:${char._id}`,
              text: char.use_name
            };
            return opt;
          }));
        } 
      } else {
        if (obj instanceof Character && obj.campaign) {
          // This is Character Inventory, viewed from a Character Sheet of a Character in a Campaign.
          // That means we want to add the Send to Campaign Inventory option.
          // We also want to add the ability to send to any other Character in the Campaign (through Mail).
          options.push({ id: "Campaign", text: "Send to Campaign Inventory" });
          options.push(...obj.campaign.characters.filter(o => o._id !== obj._id).map(char => {
            const opt: DisablableOption = {
              id: `char_id:${char._id}`,
              text: char.use_name
            };
            return opt;
          }));
        }
      }
    }
    return (
      <div>
        { options.length > 1 ?
          <SelectStringBox 
            name="Move To"
            value={""}
            options={options}
            small
            disabled={processing}
            onChange={ async (changed: string) => {
              setProcessing(true);
              await connect_util.moveItem(obj, inventory, item, changed);
              char_util.recalcWeights(obj);
              setPing(!ping);
            }}
          />
        : options.length === 1 &&
          <DandDButtonBox
            name={ `Move To ${options[0].text}` }
            small
            // log_it
            disabled={processing}
            onClick={ async () => {
              setProcessing(true);
              await connect_util.moveItem(obj, inventory, item, options[0].id);
              char_util.recalcWeights(obj);
            }}
          />
        }
      </div>
    );
  }

  const renderActive = () => {
    const can_equip = obj instanceof Campaign ? false : char_util.canEquip(obj, item);
    return (
      <div className="centered">
        { !(obj instanceof Campaign) && inventory instanceof CharacterInventory &&
          <>
            { item.base_item && 
              item.base_item.item_type === "Armor" && item.base_item instanceof Armor ? 
              <CheckBox 
                name=""
                value={item.equipped} 
                tooltip={
                  !can_equip ? 
                  "You are not proficient in this item" : 
                  ((item.base_item.armor_type_name === "Shield" && obj.shield_carried) ? 
                    "You already have a Shield equipped" : 
                    ((item.base_item.armor_type_name !== "Shield" && obj.armor_worn) ? 
                      "You already have Armor equipped": ""
                    )
                  )
                }
                disabled={ !item.equipped && (!can_equip || (item.base_item.armor_type_name === "Shield" && obj.shield_carried) || (item.base_item.armor_type_name !== "Shield" && obj.armor_worn)) }
                onChange={(changed: boolean) => {
                  const char_item = inventory.items.find(o => o.true_id === item.true_id);
                  if (char_item) {
                    if (changed) {
                      connect_util.equipItem(obj, char_item);
                    } else {
                      connect_util.unequipItem(obj, char_item);
                    }
                    setPing(!ping);
                  }
                }} 
              />
            : item.equippable ? 
              <CheckBox 
                name=""
                value={item.equipped} 
                disabled={!item.equipped && !can_equip}
                onChange={(changed: boolean) => {
                  const char_item = inventory.items.find(o => o.true_id === item.true_id);
                  if (char_item) {
                    if (changed) {
                      connect_util.equipItem(obj, char_item);
                    } else {
                      connect_util.unequipItem(obj, char_item);
                    }
                    setPing(!ping);
                  }
                }} 
              />
            :
              <span>&nbsp;--</span>
            }
          </>
        }
      </div>
    );
  }

  const renderName = () => {
    return (
      <div onClick={() => {
        if (!disabled) {
          onOpen(item);
        }
      }}>
        { <ViewItem item={item} character={obj instanceof Character ? obj : undefined} /> }
      </div>
    );
  }
  
  if (log_it) {
    console.log(item);
  }
  const render = () => {
    // if (item.name.includes("Bloodwe")) {
    //   debugger;
    // }
    // console.log(item.name);
    return (
      <Grid container spacing={0} direction="row" className={cx(classes.root)}>
        <Grid size={6}>
          <Grid container spacing={0} direction="row">
            <Grid size={2}>{ renderActive() }</Grid>
            <Grid size={6}>{ renderName() }</Grid>
            <Grid size={2}>{ renderTake() }</Grid>
            <Grid size={2}>{ renderMoveTo() }</Grid>
          </Grid>
        </Grid>
        <Grid size={6}>
          <Grid container spacing={0} direction="row">
            <Grid size={3} className="centered">
              { (item.weight > 0 ? <span>{ item.weight }</span> : <span>--</span>) }
            </Grid>
            <Grid size={3} className="centered">
              { <span>{ item.count }</span> }
            </Grid>
            <Grid size={3} className="centered">
              { (item.cost > 0 ? <span>{ item.cost }</span> : <span>--</span>) }
            </Grid>
            <Grid size={3} className="centered">
              { item.item_type }
            </Grid>
          </Grid>
        </Grid>
        {/* { renderExtras() } */}
      </Grid>
    );
  }

  return (
    <div className="padded">{ render() }</div>
  );
}
